import {
    getCoopAgents,
    getDecreasingProducers,
    getIncreasingProducers,
    getNewProducers,
    getProficiencyMetrics,
} from "../../service/proficiency-metrics";
import { agentProductionTerms as apt, TimePeriods } from "../../constants";
import { views } from "./savedAgents";
import { getChangeWord, getIncDecProducerTitle, toNegativeString } from '../components/suggestedAgents/helpers/helpers';
import { getCompactDollars } from '../../agent-production/helpers/dataFormatters';

export const terms = Object.freeze({
    adjustFilters: 'Adjust filters or check back again soon.',
    afterAnInactivityPeriodOf: 'After an inactivity period of',
    agentsWhoClosedATransaction: (timePeriod) => `Agents who have closed a transaction with your office in the ${timePeriod}`,
    agentsWhoHaveNewProduction: (timePeriod, inactivityPeriod) => `Agents who have new production over the ${timePeriod} after an inactivity period of ${inactivityPeriod}`,
    coopAgents: 'Cooperating Agents',
    decProducers: 'Decreasing Producers',
    getLastXDaysLabel: (days) => `Last ${days} Days`,
    hasNewProductionOver: 'Has new production over',
    inactivity: 'inactivity',
    incProducers: 'Increasing Producers',
    l12m: 'Last 12 months',
    l12mVsPrev12Monts: 'Last 12 months vs. previous 12 months',
    l60d: 'Last 60 days',
    l90d: 'Last 90 days',
    l90dVsSameLastYear: 'Last 90 days vs. same time last year',
    l180d: 'Last 180 days',
    lowestPosition: 'Lowest Position',
    maxIncrease: 'Maximum Increase',
    minIncrease: 'Minimum Increase',
    maxDecrease: 'Maximum Decrease',
    minDecrease: 'Minimum Decrease',
    newProducers: 'New Producers',
    noTabResultsFound: (tabTitle) => `No ${tabTitle} Found`,
    oneYear: '1 year',
    refreshSearch: 'Refresh Search',
    suggestedAgents: 'Suggested Agents',
    timeFrame: 'Time Frame',
    timePeriod: 'Time Period',
    topProducers: 'Top Producers',
    twoYears: '2 years',
    ytdVsPrevYtd: 'Year to date vs. previous year to date',
    highestProducingAgents: (producingAgentsAmount) => `The ${producingAgentsAmount} highest producing agents.`,
    production: 'Production',
    increase: 'increase',
    decrease: 'decrease',
    incDecTitle: (isIncreasingProducers, rangeText, timePeriod) => `Agents with a production ${getChangeWord(isIncreasingProducers)} ${rangeText} over the ${timePeriod}.`,
    incDecBetween: (minGrowth, maxGrowth) => `between ${minGrowth}% and ${maxGrowth}%`,
    incDecMinOnly: (minGrowth) => `of ${minGrowth}% or more`,
    incDecMaxOnly: (maxGrowth) => `of up to ${maxGrowth}%`,
});

export const todaysPicksTerms = Object.freeze({
    todaysPicks: "Today's Picks",
    todaysPicksDescription: 'Top agent recommendations based on likelihood of movement.',
    failedLoad: 'Failed to load Today\'s Picks',
    noTodaysPicksTitle: 'No Todays Picks Found',
    noTodaysPicksBody: 'Check back again soon for new recommendations.',
    likelihoodChartTitle: 'Likelihood of Movement',
    volume: 'Volume',
    units: 'Units',
    projected: 'Projected',
    low:'Low',
    medium:'Medium',
    high:'High',
    eoyPerformanceProjections: 'EOY PERFORMANCE PROJECTIONS',
    projectedRange: (projectedVolumeMin, projectedVolumeMax) => `${getCompactDollars(projectedVolumeMin)} to ${getCompactDollars(projectedVolumeMax)}`,
    projectedUnitsRange: (projectedUnitsMin, projectedUnitsMax) => `${projectedUnitsMin} to ${projectedUnitsMax} Units`,
})

export const editCriteriaTerms = Object.freeze({
    editCriteria: 'Edit Criteria',
    mls: 'MLS',
    timePeriod: 'Time Period',
    volume: 'Volume',
    units: 'Units',
    propertyTypes: 'Property Types',
    cancel: 'Cancel',
    save: 'Save',
    selectMls: 'Select MLS',
    selectTimePeriod: 'Select Time Period',
    selectVolume: 'Select Volume',
    selectUnits: 'Select Units',
    selectPropertyTypes: 'Select Property Types',
    mlsKey: 'mls',
    timePeriodKey: 'timePeriod',
    volumeKey: 'volume',
    unitsKey: 'units',
    propertyTypesKey: 'propertyTypes',
    allPropertyTypes: 'All property types',
    propertyTypesLabel: 'Property Type(s)',
    marketArea: 'Market Area (Transaction count)',
    areaKey: 'area',
});

export const areaSelectDropdown = Object.freeze({
    itemHeight: 35,
    maxMenuHeight: 300,
    defaultAreaType: 'city',
});

export const popoverIds = Object.freeze({
    display: 'display-dropdown',
    metrics: 'metrics-dropdown',
    producers: 'producers-dropdown',
    growthAnalysisPeriod: 'growth-analysis-period',
});

export const popoverTerms = Object.freeze({
    bottomEnd: 'bottom-end',
    showDisplayPopover: 'showDisplayPopover',
    showProducersPopover: 'showProducersPopover',
});

export const filterIds = Object.freeze({
    display: 'display',
    metrics: 'metrics',
    producers: 'producers',
    growthAnalysisPeriod: 'growthAnalysisPeriod',
});

export const tabs = Object.freeze({
    todaysPicks: {
        id: 'todaysPicks',
        label: todaysPicksTerms.todaysPicks,
        index: 0,
        description: todaysPicksTerms.todaysPicksDescription,
        hasRefreshSearchBtn: false,
        badgeCount: 8,
    },
    topProducers: {
        id: 'topProducers',
        label: terms.topProducers,
        index: 1,
        description: ({ highProducingAgents }) => terms.highestProducingAgents(highProducingAgents),
        hasRefreshSearchBtn: false,
    },
    incProducers: {
        id: 'incProducers',
        label: terms.incProducers,
        index: 2,
        description: ({ timePeriod, minGrowth, maxGrowth }) => getIncDecProducerTitle(true, timePeriod, minGrowth, maxGrowth),
        hasRefreshSearchBtn: true,
        inputMaxLength: 3,
    },
    decProducers: {
        id: 'decProducers',
        label: terms.decProducers,
        index: 3,
        description: ({ timePeriod, minGrowth, maxGrowth }) => getIncDecProducerTitle(false, timePeriod, minGrowth, maxGrowth),
        hasRefreshSearchBtn: true,
        inputMaxLength: 3,
    },
    newProducers: {
        id: 'newProducers',
        label: terms.newProducers,
        index: 4,
        description: ({ timePeriod, inactivityPeriod }) => terms.agentsWhoHaveNewProduction(timePeriod, inactivityPeriod),
        hasRefreshSearchBtn: true,
    },
    coopAgents: {
        id: 'coopAgents',
        label: terms.coopAgents,
        index: 5,
        description: ({ timePeriod }) => terms.agentsWhoClosedATransaction(timePeriod),
        hasRefreshSearchBtn: true,
    },
});

export const searchfilterStateId = Object.freeze({
    inactivity: 'inactivity',
    lowPos: 'lowPos',
    mls: 'mls',
    minGrowth: 'minGrowth',
    maxGrowth: 'maxGrowth',
    newProd: 'newProd',
    selectedAgentsView: 'selectedAgentsView',
    selectedView: 'selectedView',
    timeFrame: 'timeFrame',
    timePeriod: 'timePeriod',
    value: 'value',
    growthTimeFrame: 'growthTimeFrame',
});

export const producerOptions = Object.freeze({
    lowPos: [
        {
            value: "lowPos25",
            label: "25"
        },
        {
            value: "lowPos50",
            label: "50"
        },
        {
            value: "lowPos75",
            label: "75"
        },
        {
            value: "lowPos100",
            label: "100"
        }
    ],
    inactivity: [
        {
            value: "12",
            label: terms.oneYear
        },
        {
            value: "24",
            label: terms.twoYears
        },
    ],
    newProd: [
        {
            value: "Last60Days",
            label: terms.l60d
        },
        {
            value: "Last90Days",
            label: terms.l90d
        },
        {
            value: "Annually",
            label: terms.l12m
        },
    ],
    timeFrame: [
        {
            value: TimePeriods[apt.annually],
            label: terms.l12mVsPrev12Monts
        },
        {
            value: TimePeriods[apt.YTD],
            label: terms.ytdVsPrevYtd
        }
    ],
    timePeriod: [
        {
            value: "Last90Days",
            label: terms.l90d
        },
        {
            value: "Last180Days",
            label: terms.l180d
        },
        {
            value: "Annually",
            label: terms.l12m
        },
    ],
});

export const growthAnalysisIncDecTimeFrame = [
    {
        value: TimePeriods[apt.last90Days],
        label: terms.l90dVsSameLastYear
    },
    ...producerOptions.timeFrame
];

export const searchConfigFallback = {
    getApiService: () => ({
        recruitingSoldResponse: [],
        recruitingSoldTotals: {},
    }),
    getProducerValues: () => ({}),
    getParameters: () => ([]),
};

const incDecProducersConfig = {
    getProducerValues: (producerOptions, isGrowthAnalysis) => ({
        timePeriod: isGrowthAnalysis
            ? producerOptions?.growthTimeFrame?.value
            : producerOptions?.timeFrame?.value,
        minGrowth: producerOptions?.minGrowth,
        maxGrowth: producerOptions?.maxGrowth
    })
};

const incDecProducersTableSettings = {
    shouldExcludeZeroGrowthAgents: true,
};

export const searchConfigByTabId = {
    [tabs.topProducers.id]: {
        getApiService: getProficiencyMetrics,
        getProducerValues: (producerOptions) => ({
            lowPos: producerOptions?.lowPos?.value,
        }),
        getParameters: () => ([
            {
                parameter: 'limit',
                value: '100',
            }
        ]),
    },
    [tabs.newProducers.id]: {
        getApiService: getNewProducers,
        getProducerValues: (producerOptions) => ({
            newProd: producerOptions?.newProd?.value,
            inactivity: producerOptions?.inactivity?.value,
        }),
        getParameters: ({inactivity}) => ([
            {
                parameter: 'monthsInactive',
                value: inactivity,
            }
        ]),
    },
    [tabs.coopAgents.id]: {
        getApiService: getCoopAgents,
        getProducerValues: (producerOptions) => ({
            timePeriod: producerOptions?.timePeriod?.value,
        }),
    },
    [tabs.incProducers.id]: {
        getApiService: getIncreasingProducers,
        ...incDecProducersConfig,
        getParameters: ({ minGrowth, maxGrowth }) => {

            const parameters = [];
            parameters.push({
                parameter: searchfilterStateId.minGrowth,
                value: minGrowth || '0',
            });
            if (maxGrowth) {
                parameters.push({
                    parameter: searchfilterStateId.maxGrowth,
                    value: maxGrowth,
                });
            }

            return parameters;
        }
    },
    [tabs.decProducers.id]: {
        getApiService: getDecreasingProducers,
        ...incDecProducersConfig,
        getParameters: ({ minGrowth, maxGrowth }) => {
            // For decreasing producers, Maximum Decrease % is minGrowth parameter
            // Minimum Decrease % is maxGrowth parameter
            const parameters = [];
            if (minGrowth) {
                parameters.push({
                    parameter: searchfilterStateId.maxGrowth,
                    value: toNegativeString(minGrowth),
                });
            }
            if (maxGrowth) {
                parameters.push({
                    parameter: searchfilterStateId.minGrowth,
                    value: toNegativeString(maxGrowth),
                });
            }
    
            return parameters;
        }
    }
};

export const tableSettingsByTab = {
    [tabs.incProducers.id]: incDecProducersTableSettings,
    [tabs.decProducers.id]: incDecProducersTableSettings,
};

export const allViewOptions = [
    {
        value: views.overview,
        label: apt.overview
    },
    {
        value: views.contactInformation,
        label: apt.contactInformation
    },
    {
        value: views.growthAnalysis,
        label: apt.growthAnalysis
    },
    {
        value: views.listingProficiency,
        label: apt.listingProficiency
    }
];

export const viewOptionsByTab = Object.freeze({
    [tabs.topProducers.id]: allViewOptions,
    [tabs.incProducers.id]: allViewOptions,
    [tabs.decProducers.id]: allViewOptions,
    [tabs.newProducers.id]: [
        {
            value: views.overview,
            label: apt.overview
        },
        {
            value: views.contactInformation,
            label: apt.contactInformation
        },
        {
            value: views.listingProficiency,
            label: apt.listingProficiency
        }
    ],
    [tabs.coopAgents.id]: allViewOptions,

});

export const timeFrameToGrowthPeriod = {
    [TimePeriods[apt.last90Days]]:  apt.last90Days,
    [TimePeriods[apt.annually]]: apt.last12Months,
    [TimePeriods[apt.YTD]]: apt.yearToDate,
};

export const headerParentIndexesByView = Object.freeze({
    [views.overview]: {
        currentSearch: 0,
        allMlsTransactionsLast12Months: 1,
    },
    [views.contactInformation]: {
        contact: 0,
        currentSearch: 1,
        allMlsTransactionsLast12Months: 2,
        growth: 3
    },
    [views.growthAnalysis]: {
        officeInformation: 0,
        units: 1,
        volumne: 2,
    },
    [views.listingProficiency]: {
        officeInformation: 0,
        totalList: 1,
        noPriceChangesListings: 2,
        priceChangesListings: 3,
    },
});

const overviewHeaderParentIndexes = headerParentIndexesByView[views.overview];
const contactInfoHeaderParentIndexes = headerParentIndexesByView[views.contactInformation];

export const headerParentIndexesToRemoveByTabAndView = Object.freeze({
    [tabs.newProducers.id] : {
        [views.overview]: [
            overviewHeaderParentIndexes.allMlsTransactionsLast12Months
        ],
        [views.contactInformation]: [
            contactInfoHeaderParentIndexes.allMlsTransactionsLast12Months,
            contactInfoHeaderParentIndexes.growth,
        ]
    },
});
